import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Stack, Skeleton, Link, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import api from '../api/axios';
import { isLoggedIn } from '../utils/currentUser';
import { Ornament, OrnamentBand, STUDIO_NAME, Wordmark } from '../components/Brand';

const EMPTY_GAME = { title: '', description: '', imageUrl: '', externalUrl: '' };
const SECTION = { maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 3 } };

// ── Game dialog (create / edit) ───────────────────────────────────────────
function GameDialog({ open, initial, onClose, onSave, loading }) {
  const [form, setForm] = useState(EMPTY_GAME);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/upload', fd);
      setForm((f) => ({ ...f, imageUrl: data.url }));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEnter: () => setForm(initial ?? EMPTY_GAME) }}
    >
      <DialogTitle>{initial ? 'Edit game' : 'Add game'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" required value={form.title} onChange={set('title')} fullWidth autoFocus />
          <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline minRows={2} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField label="Cover image URL" value={form.imageUrl} onChange={set('imageUrl')} fullWidth />
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImage} />
            <Button variant="outlined" color="inherit" onClick={() => fileRef.current?.click()} disabled={uploading}
              sx={{ flexShrink: 0, height: 40, borderColor: 'divider' }}
            >
              {uploading ? <CircularProgress size={16} /> : 'Upload'}
            </Button>
          </Box>
          <TextField label="Link (Steam, itch.io…)" value={form.externalUrl} onChange={set('externalUrl')} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!form.title.trim() || loading} onClick={() => onSave(form)}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GameCard({ game, editable, onEdit, onDelete }) {
  // The whole card opens the game page; inner controls stop the click.
  const open = game.externalUrl ? () => window.open(game.externalUrl, '_blank', 'noopener') : undefined;
  return (
    <Box
      onClick={open}
      onKeyDown={(e) => { if (open && e.key === 'Enter') open(); }}
      role={open ? 'link' : undefined}
      tabIndex={open ? 0 : undefined}
      sx={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
        cursor: open ? 'pointer' : 'default', transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'rgba(235,231,225,0.22)' },
        '&:hover .game-actions': { opacity: 1 },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Box sx={{ aspectRatio: '16 / 9', bgcolor: '#1f1d1b', position: 'relative' }}>
        {game.imageUrl ? (
          <Box component="img" src={game.imageUrl} alt={game.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', opacity: 0.5 }}>
            <Ornament size={40} />
          </Box>
        )}
        {editable && (
          <Box className="game-actions" sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.15s' }}>
            {[[EditOutlinedIcon, 'Edit', onEdit], [DeleteOutlineIcon, 'Delete', onDelete]].map(([Icon, label, fn]) => (
              <Tooltip key={label} title={label}>
                <IconButton onClick={(e) => { e.stopPropagation(); fn(); }} sx={{ bgcolor: 'rgba(18,18,18,0.85)', '&:hover': { bgcolor: 'rgba(18,18,18,1)' } }}>
                  <Icon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
        <Typography fontWeight={600} fontSize="1rem">{game.title}</Typography>
        {game.description && (
          <Typography variant="body2" color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.55 }}
          >
            {game.description}
          </Typography>
        )}
        {game.externalUrl && (
          <Link href={game.externalUrl} target="_blank" rel="noopener" underline="hover" onClick={(e) => e.stopPropagation()}
            sx={{ mt: 'auto', pt: 0.5, fontSize: '0.84rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
          >
            Visit page <NorthEastIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
      </Box>
    </Box>
  );
}

export default function Home() {
  const qc = useQueryClient();
  const authed = isLoggedIn();
  const [dialog, setDialog] = useState({ open: false, game: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: games = [], isLoading } = useQuery({
    queryKey: ['games'],
    queryFn: () => api.get('/games').then((r) => r.data),
  });

  const closeDialog = () => setDialog({ open: false, game: null });
  const onSaved = () => { qc.invalidateQueries({ queryKey: ['games'] }); closeDialog(); };
  const createGame = useMutation({ mutationFn: (data) => api.post('/games', data), onSuccess: onSaved });
  const updateGame = useMutation({ mutationFn: ({ id, ...data }) => api.put(`/games/${id}`, data), onSuccess: onSaved });
  const deleteGame = useMutation({
    mutationFn: (id) => api.delete(`/games/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['games'] }); setDeleteTarget(null); },
  });

  const handleSave = (form) => {
    if (dialog.game) updateGame.mutate({ id: dialog.game.id, ...form });
    else createGame.mutate(form);
  };

  return (
    <Box sx={{ color: 'text.primary' }}>
      {/* Visitors get a minimal header; signed-in users already have the top bar. */}
      {!authed && (
        <Box component="header" sx={{ ...SECTION, height: 56, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Wordmark />
          <Box sx={{ flex: 1 }} />
          {[['Games', '#games'], ['Studio', '#studio']].map(([label, href]) => (
            <Link key={href} href={href} underline="none" color="text.secondary" sx={{ fontSize: '0.86rem', '&:hover': { color: 'text.primary' } }}>
              {label}
            </Link>
          ))}
        </Box>
      )}

      {/* ── Hero ── */}
      <Box component="section" sx={{ ...SECTION, pt: { xs: 8, md: 14 }, pb: { xs: 7, md: 12 } }}>
        <Typography variant="overline" color="primary.main" sx={{ display: 'block', mb: 2 }}>
          Independent game studio
        </Typography>
        <Typography component="h1" sx={{
          fontSize: { xs: '2.6rem', sm: '3.6rem', md: '4.6rem' }, fontWeight: 600,
          letterSpacing: '-0.035em', lineHeight: 1.02, mb: 3,
        }}>
          {STUDIO_NAME}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: { xs: '1.02rem', md: '1.15rem' }, lineHeight: 1.65, maxWidth: 560, mb: 4 }}>
          We make games!
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button variant="contained" href="#games" size="large">See our games</Button>
          <Button color="inherit" href="#studio" size="large" sx={{ color: 'text.secondary' }}>About the studio</Button>
        </Stack>
      </Box>

      <OrnamentBand />

      {/* ── Games ── */}
      <Box component="section" id="games" sx={{ ...SECTION, py: { xs: 7, md: 10 }, scrollMarginTop: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 4 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>Games</Typography>
          {games.length > 0 && <Typography color="text.disabled">{games.length}</Typography>}
          <Box sx={{ flex: 1 }} />
          {authed && (
            <Button variant="outlined" color="inherit" startIcon={<AddIcon />} sx={{ borderColor: 'divider' }}
              onClick={() => setDialog({ open: true, game: null })}
            >
              Add game
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2.5 }}>
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={280} />)}
          </Box>
        ) : games.length === 0 ? (
          <Box sx={{ py: 6, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography color="text.secondary">Our first game is in the works. Check back soon.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2.5 }}>
            {games.map((game) => (
              <GameCard key={game.id} game={game} editable={authed}
                onEdit={() => setDialog({ open: true, game })}
                onDelete={() => setDeleteTarget(game)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Studio ── */}
      <Box component="section" id="studio" sx={{ borderTop: '1px solid', borderColor: 'divider', scrollMarginTop: 56 }}>
        <Box sx={{ ...SECTION, py: { xs: 7, md: 10 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' }, gap: { xs: 3, md: 8 } }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>The studio</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography color="text.secondary" sx={{ lineHeight: 1.75, fontSize: '1rem' }}>
              We are a small team, so every project gets close attention — from the first
              prototype to the last patch.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ ...SECTION, py: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled', fontSize: '0.8rem' }}>
          <Box sx={{ color: 'primary.main', opacity: 0.7 }}><Ornament size={12} /></Box>
          © {new Date().getFullYear()} {STUDIO_NAME}
        </Box>
      </Box>

      <GameDialog open={dialog.open} initial={dialog.game} onClose={closeDialog} onSave={handleSave}
        loading={createGame.isPending || updateGame.isPending}
      />
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete “{deleteTarget?.title}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">It will disappear from the homepage. This can’t be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancel</Button>
          <Button onClick={() => deleteGame.mutate(deleteTarget.id)} color="error" variant="contained" disabled={deleteGame.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
